<template>
    <card-list-item @click="$emit('click')" :class="{'active': active, 'has-unread': ticket.has_unread}">
        <template v-slot:icon>
            <i class="fa-fw" :class="statusIcon"></i>
        </template>
        <template v-slot:content>
            <div class="tile-title">{{ticket.subject}}</div>
            <div class="domain-tag" v-if="ticket.domain_name" :style="domainTagStyle">{{ticket.domain_name}}</div>
            <div class="meta"><i class="far fa-clock fa-fw mr-2"></i>{{timeAgo}}</div>
            <div class="meta"><i class="fas fa-fw mr-2" :class="channelTypeIcon"></i>{{senderDisplay}}</div>
            <div class="meta" v-if="ticket.user_first_name"><i class="fas fa-user fa-fw mr-2"></i>{{ticket.user_first_name}} {{ticket.user_last_name}}</div>
            <div class="viewing-indicator" v-if="viewers.length">
                <i class="fas fa-eye mr-1"></i>
                <span v-for="(v, i) in viewers" :key="v.userId">{{v.name}}<span v-if="i < viewers.length - 1">, </span></span>
            </div>
        </template>
        <template v-slot:status>
            <div class="stats">
                <span class="stat unread-badge" v-if="ticket.has_unread">NEW</span>
                <span class="stat priority-badge" :class="'priority-' + ticket.priority" v-if="ticket.priority !== 'normal'">{{ticket.priority}}</span>
                <span class="stat" v-if="ticket.message_count"><i class="far fa-comments"></i>{{ticket.message_count}}</span>
            </div>
        </template>
        <template v-slot:details>
            <div class="tile-details">
                <toolbar class="toolbar">
                    <b-tooltip label="View Ticket"><button class="icon-only" @click.stop="$emit('click')"><i class="far fa-eye"></i></button></b-tooltip>
                    <b-tooltip label="Assign to Me" v-if="!ticket.assigned_to"><button class="icon-only" @click.stop="$emit('assign')"><i class="far fa-hand-point-up"></i></button></b-tooltip>
                    <b-tooltip label="Close" v-if="ticket.status !== 'closed'"><button class="icon-only" @click.stop="$emit('close')"><i class="far fa-check"></i></button></b-tooltip>
                </toolbar>
                <div class="data mt-1">
                    <data-row mini title="ID">#{{ticket.id}}</data-row>
                    <data-row mini title="Assigned" v-if="ticket.assigned_first_name">{{ticket.assigned_first_name}} {{ticket.assigned_last_name}}</data-row>
                    <data-row mini title="Status">{{ticket.status}}</data-row>
                </div>
            </div>
        </template>
    </card-list-item>
</template>

<script>
    import Toolbar from "@/TIER/components/Toolbar";
    import DataRow from "@/TIER/components/DataRow";
    import CardListItem from "@/TIER/components/CardListItem";
    import moment from 'moment';
    import {getViewers} from '@/components/supportViewingTracker';

    export default {
        name: 'SupportTicketTile',
        components: {CardListItem, DataRow, Toolbar},
        props: {
            card: { required: true },
            ticket: { type: Object, required: true },
            active: { type: Boolean, default: false }
        },
        computed: {
            viewers() {
                let me = this.$store ? this.$store.getters.currentUser : null;
                return getViewers(this.ticket.id, me ? me.id : null);
            },
            statusIcon() {
                let icon;
                switch (this.ticket.channel_type) {
                    case 'sms': icon = 'fa-sms'; break;
                    case 'direct': icon = 'fa-comment-dots'; break;
                    default: icon = 'fa-envelope';
                }
                switch (this.ticket.status) {
                    case 'open': return 'fas ' + icon + ' text-danger';
                    case 'assigned': return 'fas ' + icon + ' text-warning';
                    case 'on_hold': return 'fas ' + icon + ' text-info';
                    case 'closed': return 'far ' + icon + ' text-success';
                    default: return 'far ' + icon;
                }
            },
            channelTypeIcon() {
                switch (this.ticket.channel_type) {
                    case 'sms': return 'fa-sms';
                    case 'direct': return 'fa-comment-dots';
                    default: return 'fa-envelope';
                }
            },
            senderDisplay() {
                switch (this.ticket.channel_type) {
                    case 'sms': return this.ticket.phone_from || 'Unknown';
                    case 'direct': return this.ticket.user_first_name ? (this.ticket.user_first_name + ' ' + this.ticket.user_last_name) : 'Direct';
                    default: return this.ticket.email_from || 'Unknown';
                }
            },
            domainTagStyle() {
                if (!this.ticket.domain_color) return {};
                return {
                    background: this.ticket.domain_color,
                    color: '#fff'
                };
            },
            timeAgo() {
                if (!this.ticket.last_message_at) return '';
                return moment(this.ticket.last_message_at).fromNow();
            }
        }
    }
</script>

<style lang="scss" scoped>
    .has-unread {
        border-left: 3px solid #dc3545;
        background: #fff6f6;

        .tile-title {
            color: #b21f2d;
        }
    }
    .unread-badge {
        font-size: 0.7rem;
        text-transform: uppercase;
        font-weight: 700;
        padding: 1px 6px;
        border-radius: 3px;
        background: #dc3545;
        color: #fff;
    }
    .tile-title {
        font-size: 0.9rem;
        font-weight: bold;
    }
    .tile-details {
        margin-top: 0.5rem;
    }
    .priority-badge {
        font-size: 0.7rem;
        text-transform: uppercase;
        font-weight: 600;
        padding: 1px 6px;
        border-radius: 3px;
    }
    .priority-urgent {
        background: #dc3545;
        color: #fff;
    }
    .priority-high {
        background: #fd7e14;
        color: #fff;
    }
    .priority-low {
        color: #6c757d;
    }
    .domain-tag {
        display: inline-block;
        font-size: 0.7rem;
        font-weight: 600;
        padding: 1px 6px;
        border-radius: 3px;
        background: #e8eef4;
        color: #28547F;
        margin-top: 2px;
    }
    .viewing-indicator {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        color: #856404;
        background: #fff3cd;
        padding: 2px 6px;
        border-radius: 3px;
        margin-top: 3px;
    }
</style>
